import type { LtiClientConnection } from "@elaso/shared/types/lti";
import { deleteApp, initializeApp } from "firebase/app";
import {
	type Auth,
	createUserWithEmailAndPassword,
	getAuth,
	onAuthStateChanged,
	signInWithCustomToken,
	signInWithEmailAndPassword,
	signOut,
	type User,
} from "firebase/auth";

const firebaseConfigBase64 = import.meta.env
	.VITE_FIREBASE_CONFIG_BASE64 as string;

if (!firebaseConfigBase64) {
	throw new Error(
		"VITE_FIREBASE_CONFIG_BASE64 environment variable is not set"
	);
}

const firebaseConfig = JSON.parse(atob(firebaseConfigBase64));

/**
 * The one sign-in every ELASO client shares: the Learner Client, the Operations
 * Client and both admin clients
 * (docs/adr/40-every-elaso-client-shares-one-sign-in.md). What each client lets
 * the signed-in user open is decided by that client, never by this.
 */
export type FirebaseSession = {
	login: (email: string, password: string) => Promise<unknown>;
	signup: (email: string, password: string) => Promise<unknown>;
	/**
	 * Sign in with a server-minted custom token, and report which Firebase user
	 * that turned out to be (TECH-3186, the LTI landing route).
	 *
	 * The uid is returned rather than left to be read off `getCurrentUserId`,
	 * because this is the one sign-in path that can run while ANOTHER account is
	 * already signed in. The caller compares the two and tears down the previous
	 * account's local state. See `lib/lti/launch-flow.ts`.
	 */
	loginWithCustomToken: (token: string) => Promise<string>;
	logout: () => Promise<void>;
	subscribeToAuthChanges: (callback: (user: User | null) => void) => () => void;
	getIdToken: () => Promise<string | null>;
	/**
	 * An ID token together with WHOSE it is, from one snapshot of `currentUser`
	 * (TECH-3247).
	 *
	 * `getIdToken` awaits inside, so a caller that also needs the uid had to read
	 * `getCurrentUserId()` afterwards, and a `signInWithCustomToken` can land in
	 * that window and replace the answer. The socket-identity axis in
	 * `lib/socket-owner.ts` cannot survive that drift.
	 */
	getIdTokenWithUid: () => Promise<{ idToken: string; uid: string } | null>;
	getCurrentUserId: () => string | null;
};

// The DEFAULT, unnamed, Firebase app. Firebase namespaces persisted auth state by
// app name (`firebase:authUser:{apiKey}:{appName}`), so naming this app would
// sign out every already-signed-in user on deploy.
const home = getAuth(initializeApp(firebaseConfig));
let auth = home;

/**
 * Tabs tell each other whose the home sign-in is (TECH-3504). Firebase carries
 * a sign-in or a sign-out to every tab itself, with one exception: a sign-in
 * from another identity home. A tab signed in at a different pool refuses to
 * take it (`tenant-id-mismatch`) and goes on holding the account before it,
 * silently. An LTI launch on a shared machine is exactly that sign-in.
 */
const HOME_ACCOUNT_CHANNEL = "elaso:home-account";
const homeAccountChannel =
	typeof BroadcastChannel === "undefined"
		? null
		: new BroadcastChannel(HOME_ACCOUNT_CHANNEL);

onAuthStateChanged(home, (user) => {
	homeAccountChannel?.postMessage({ uid: user?.uid ?? null });
});

/**
 * The account another tab says is signed in at home: a uid, `null` for nobody,
 * or `undefined` for a message that names no account.
 */
const announcedUid = (data: unknown): string | null | undefined => {
	const uid = (data as { uid?: unknown } | null)?.uid;
	return typeof uid === "string" || uid === null ? uid : undefined;
};
const listeners = new Map<(user: User | null) => void, () => void>();

const attach = (next: Auth): void => {
	auth = next;
	for (const [callback, unsubscribe] of listeners) {
		unsubscribe();
		listeners.set(callback, onAuthStateChanged(next, callback));
	}
};

export const firebaseSession: FirebaseSession = {
	login: (email, password) => signInWithEmailAndPassword(auth, email, password),
	signup: (email, password) =>
		createUserWithEmailAndPassword(auth, email, password),
	loginWithCustomToken: async (token) => {
		const credential = await signInWithCustomToken(auth, token);
		return credential.user.uid;
	},
	logout: () => signOut(auth),
	subscribeToAuthChanges: (callback) => {
		listeners.set(callback, onAuthStateChanged(auth, callback));
		return () => {
			listeners.get(callback)?.();
			listeners.delete(callback);
		};
	},
	getIdToken: async () => {
		const user = auth.currentUser;
		if (!user) {
			return null;
		}
		return await user.getIdToken();
	},
	getIdTokenWithUid: async () => {
		const user = auth.currentUser;
		if (!user) {
			return null;
		}
		return { idToken: await user.getIdToken(), uid: user.uid };
	},
	getCurrentUserId: () => auth.currentUser?.uid ?? null,
};

/**
 * The named Firebase apps `/operations` and `/organization` signed in on before
 * every client shared one sign-in
 * (docs/adr/40-every-elaso-client-shares-one-sign-in.md). Their persisted users are
 * credentials no screen can sign out of any more, so each start-up signs out
 * whoever they restore and deletes them again. Remove once no browser can still
 * hold one.
 */
const RETIRED_APP_NAMES = ["operations", "organization"] as const;

const retire = async (name: string): Promise<void> => {
	const app = initializeApp(firebaseConfig, name);
	try {
		const retired = getAuth(app);
		await retired.authStateReady();
		if (retired.currentUser) {
			await signOut(retired);
		}
	} finally {
		await deleteApp(app);
	}
};

export const retireSplitSessions = async (): Promise<void> => {
	const outcomes = await Promise.allSettled(RETIRED_APP_NAMES.map(retire));
	const failed = outcomes.find(
		(outcome): outcome is PromiseRejectedResult => outcome.status === "rejected"
	);
	if (failed) {
		throw failed.reason;
	}
};

let instanceSignIns = 0;

/** An account signed in at another ELASO Instance's Firebase project, not yet the sign-in's. */
export type InstanceSignIn = {
	uid: string;
	getIdToken: () => Promise<string>;
	adopt: () => void;
	discard: () => Promise<void>;
};

/**
 * Sign in with a custom token another ELASO Instance minted, on a Firebase app of
 * that Instance's own project, without touching the sign-in. `adopt` then makes
 * it the sign-in, which is what switches the client there.
 */
export const signInAtInstance = async ({
	firebase,
	tenantId,
	customToken,
}: {
	firebase: LtiClientConnection["firebase"];
	tenantId: string;
	customToken: string;
}): Promise<InstanceSignIn> => {
	instanceSignIns += 1;
	const instanceApp = initializeApp(
		firebase,
		`instance:${firebase.projectId}:${instanceSignIns}`
	);
	const instanceAuth = getAuth(instanceApp);
	instanceAuth.tenantId = tenantId;
	let user: User;
	try {
		({ user } = await signInWithCustomToken(instanceAuth, customToken));
	} catch (error) {
		await deleteApp(instanceApp);
		throw error;
	}
	return {
		uid: user.uid,
		getIdToken: () => user.getIdToken(),
		adopt: () => attach(instanceAuth),
		discard: async () => {
			try {
				await signOut(instanceAuth);
			} finally {
				await deleteApp(instanceApp);
			}
		},
	};
};

export const homeSession = {
	getIdToken: async (): Promise<string | null> =>
		(await home.currentUser?.getIdToken()) ?? null,
	adopt: (): void => attach(home),
	signOut: (): Promise<void> => signOut(home),
	/** The account signed in here, whichever sign-in the client is using. */
	uid: (): string | null => home.currentUser?.uid ?? null,
	/**
	 * Calls back with whose the home sign-in is, a uid or `null` once signed
	 * out, whenever it may have changed, in this tab or in another. One change
	 * can be reported more than once. Returns the unsubscribe.
	 */
	onAccountChanged: (callback: (uid: string | null) => void): (() => void) => {
		const stopHere = onAuthStateChanged(home, (user) =>
			callback(user?.uid ?? null)
		);
		const hear = (event: MessageEvent): void => {
			const uid = announcedUid(event.data);
			if (uid !== undefined) callback(uid);
		};
		homeAccountChannel?.addEventListener("message", hear);
		return () => {
			stopHere();
			homeAccountChannel?.removeEventListener("message", hear);
		};
	},
};

/**
 * Sign in, from here on, at ONE identity home's pool of Identities
 * (docs/adr/33-identity-homes-sign-in-chosen-by-the-address.md, TECH-3350).
 *
 * Called BEFORE any credential is entered — the resolver answers before the form
 * is rendered — so no sign-in can race a sign-in that has not been pointed yet.
 * A member of another Organisation then fails here exactly as a wrong password
 * fails, which is the record's *No probing* reaching the form.
 *
 * It does NOT move an already-signed-in user. Firebase reads `tenantId` when a
 * credential is exchanged, so a restored sign-in keeps whatever pool it was
 * minted against. Refusing a token whose tenant is not this address's is
 * TECH-3354's.
 */
export const setSignInTenant = (tenantId: string): void => {
	auth.tenantId = tenantId;
};
